import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { Endpoints } from '@lifeomic/react-client/generated/medical-results-service';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import urls from '../util/urls';
import { ServiceAuth } from '../hooks/useService/useService';
import { pick } from 'lodash';

interface Options {
  axiosOptions?: AxiosRequestConfig;
  [key: string]: any;
}

// --- Published types from @lifeomic/react-client (medical-results-service) ---

export type HistoryResponse =
  Endpoints['GET /v1/medical-results/patient/:patientId/history']['Response'];

export type V3Response =
  Endpoints['GET /v1/medical-results/v3/patient/:patientId/results']['Response'];

/**
 * Derived from the generated medical-results response rather than restated, so
 * it cannot fall behind the service. It was previously hand-written here and
 * intersected onto the response items; the response has carried `source`
 * natively for some time, so the intersection was already redundant and only
 * became visible when the service added `questionnaireResponse` and the two
 * lists disagreed.
 */
export type MedicalResultSource = NonNullable<
  HistoryResponse['items'][0]['source']
>;

export type ResultHistory = HistoryResponse['items'][0];

export type ResultHistoryWithRange = Extract<
  ResultHistory,
  { type: 'withRange' }
>;

export type ResultHistoryWithoutRange = Exclude<
  ResultHistory,
  { type: 'withRange' }
>;

export type Ranges = ResultHistoryWithRange['ranges'];

type ResultResponseItem = V3Response['items'][0];

export type IndividualResult = ResultResponseItem['results'][0];

export type Result = Omit<ResultResponseItem, 'results'> & {
  results: IndividualResult[];
};

export type ResultWithRange = Extract<IndividualResult, { type: 'withRange' }>;

export const isResultWithRange = (
  result: IndividualResult,
): result is ResultWithRange => result.type === 'withRange';

export const isResultHistoryWithRange = (
  result: ResultHistory,
): result is ResultHistoryWithRange => result.type === 'withRange';

export type ResultCategory = {
  code: string;
  display: string;
  icon?: string;
};

export type ResultCategoriesResponse = {
  items: ResultCategory[];
};

export const MEDICAL_RESULTS_SYSTEM =
  'http://lifeomic.com/fhir/medical-results/systems';

export const DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM =
  'http://lifeomic.com/fhir/document-extraction-v2-projection';

export const getMedicalResultSource = (
  result:
    | { source?: MedicalResultSource; resource?: fhir.Observation }
    | undefined,
): MedicalResultSource | undefined => {
  if (result?.source) {
    return result.source;
  }
  return result?.resource?.identifier?.some(
    identifier =>
      identifier.system === DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
  )
    ? 'documentExtractionV2'
    : undefined;
};

export const isDocumentExtractionV2Result = (
  result:
    | { source?: MedicalResultSource; resource?: fhir.Observation }
    | undefined,
) => getMedicalResultSource(result) === 'documentExtractionV2';

interface BaseParams {
  patientId: string;
  project: string;
  nextPageToken?: string | null;
  pageSize?: number | null;
  cohort?: string;
  resultCount?: string;
  code?: string;
  system?: string;
  includeClinicOnly?: boolean;
  source?: MedicalResultSource;
  includeDocumentExtractionV2?: boolean;
  excludeMappedCodings?: boolean;
}

export default class MedicalResultsService {
  medicalResultsAPI: (auth: ServiceAuth) => AxiosInstance;
  constructor(_options?: Partial<Options>) {
    this.medicalResultsAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getResults =
    <TResponse = V3Response>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.results(
          params.patientId,
          pick(params, ['project', 'nextPageToken', 'pageSize', 'cohort']),
        ),
      );
    };

  getResultsV3 =
    <TResponse = V3Response>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.resultsV3(
          params.patientId,
          pick(params, [
            'project',
            'nextPageToken',
            'pageSize',
            'cohort',
            'resultCount',
            'includeClinicOnly',
            'source',
            'includeDocumentExtractionV2',
            'excludeMappedCodings',
          ]),
        ),
      );
    };

  getHistory =
    <TResponse = HistoryResponse>(auth: ServiceAuth) =>
    async (params: BaseParams): Promise<AxiosResponse<TResponse>> => {
      return this.medicalResultsAPI(auth).get<TResponse>(
        urls.api.medicalResults.history(
          params.patientId,
          pick(params, [
            'project',
            'cohort',
            'code',
            'system',
            'includeClinicOnly',
            'source',
            'includeDocumentExtractionV2',
            'excludeMappedCodings',
          ]),
        ),
      );
    };

  getResultCategories(auth: ServiceAuth) {
    return async (params: {
      projectId: string;
      usedOnly?: boolean;
    }): Promise<AxiosResponse<ResultCategoriesResponse>> => {
      return this.medicalResultsAPI(auth).get<ResultCategoriesResponse>(
        urls.api.medicalResults.projectResultCategories(
          params.projectId,
          params.usedOnly ? { usedOnly: true } : {},
        ),
      );
    };
  }
}
